import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import InsertDriveFileOutlined from '@mui/icons-material/InsertDriveFileOutlined';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import Box from '@mui/material/Box';
import CircularProgress, {
  circularProgressClasses,
} from '@mui/material/CircularProgress';
import Drawer, { drawerClasses } from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  FileAttachmentCategory,
  patchV1CompaniesCompanyIdJobsJobIdSummary,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';

import Label from '~components/FormFields/v2/Label';
import JobSummaryCardStat from '~components/Job/JobSummaryCardStat';
import JobSummaryEditForm, {
  JobSummaryEditFormHandler,
} from '~components/Job/JobSummaryEditForm';
import Menu, { MenuHandler } from '~components/Menu/Menu';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { FeatureFlags } from '~constants/featureFlags';
import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import { useFileAttachment } from '~hooks/useFileAttachment/useFileAttachment';
import { Job } from '~hooks/useJob/models';
import useJobTimesheets from '~hooks/useJobTimesheets/useJobTimesheets';
import useTimesheets from '~hooks/useTimesheets/useTimesheets';
import {
  fileAttachmentToListItem,
  TimesheetListItem,
  timesheetToListItem,
} from '~pages/Approvals/shared/timesheetListItem';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { minutesToDecimalHours } from '~utils/dateTime';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { DeleteTimesheetDialog } from './DeleteTimesheetDialog';

const MAX_FILE_SIZE_IN_MB = 10;
const NO_CONTENT = '-';

// AI vision extraction is async with no realtime channel, so after a new-model upload
// we poll the created timesheet until it is scanned, then prefill the job summary times.
const SCAN_POLL_INTERVAL_MS = 5000; // check scan status every 5s
const MAX_SCAN_POLL_ATTEMPTS = 12; // stop after ~1 min so a stuck extraction never polls forever

const fmtTime = (value?: Dayjs | null): string =>
  value ? dayjs.tz(value).format('h:mm A') : NO_CONTENT;

const isPdf = (fileName: string): boolean => fileName.toLowerCase().endsWith('.pdf');

// What to call a sheet on screen: the slip number the office reconciles against when it
// has one, else the uploaded file's name.
const timesheetTitle = (timesheet: TimesheetListItem): string =>
  timesheet.timesheetNumber || timesheet.fileName;

// Persistent timesheet section for the redesigned Job Summary. Always renders an
// "Add Timesheet" button (keeps existing upload functionality), lists any
// attached timesheets with a thumbnail + delete, and lives under the times in
// the Total Hours column. `use_new_timesheet` OFF reads and writes legacy
// FileAttachments in the Timesheet category; those rows have no AI extraction and no
// slip number, so that path has no scan poll, no prefill, and no number field.
// Compact timesheet cluster: a label, a thumbnail per attached file (filename on
// hover), and an add button. `hideLabel` drops the internal label when the
// caller already supplies one (e.g. a summary column header).
export default function JobTimesheetSection({
  job,
  hideLabel = false,
  onSummarySaved,
}: {
  job: Job;
  hideLabel?: boolean;
  // Fired after the reviewer saves times from the ticket drawer, so the parent
  // (JobDetails) can refetch and the hero reflects the new Start/End/Total.
  onSummarySaved?: () => void | Promise<void>;
}) {
  const theme = useTheme();
  const c = theme.brandV2.colors;
  const { toasterStore, userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  // Match legacy Approve Jobs: gate timesheet upload on canCreateTicket and delete on
  // canDeleteTicket, so unauthorized users don't get controls they previously lacked.
  const { canCreateTicket, canDeleteTicket, canEditTicket } = userStore.getPermissions();
  const useNewTimesheet = useFeatureFlag({
    featureFlagKey: FeatureFlags.useNewTimesheet,
  });
  const { createTimesheet, deleteTimesheetById, getTimesheetById, updateTimesheetById } =
    useTimesheets();
  const {
    createFileAttachmentOnJob,
    deleteFileAttachmentById,
    updateFileAttachmentById,
  } = useFileAttachment();
  const { fetchJobTimesheets } = useJobTimesheets();
  const [timesheetFiles, setTimesheetFiles] = useState<TimesheetListItem[]>([]);
  const [loadingTimesheets, setLoadingTimesheets] = useState(true);

  const updateTimesheets = useCallback((item: TimesheetListItem, remove?: true) => {
    setTimesheetFiles((prev) =>
      remove ? prev.filter((file) => file.key !== item.key) : [...prev, item],
    );
  }, []);

  useEffect(() => {
    if (!companyId) {
      setTimesheetFiles([]);
      setLoadingTimesheets(false);
      return;
    }
    const controller = new AbortController();
    setLoadingTimesheets(true);
    fetchJobTimesheets(companyId, job.id, useNewTimesheet, controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) {
          setTimesheetFiles(rows);
          setLoadingTimesheets(false);
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setLoadingTimesheets(false);
        }
      });
    return () => controller.abort();
  }, [companyId, job.id, useNewTimesheet, fetchJobTimesheets]);
  // Editing Start/End/Total from the timesheet drawer must require the same
  // job-summary edit permission as the hero's Hours pencil (job.jobSummary.editable),
  // otherwise a user blocked from the pencil could edit the times via this drawer.
  const canEditSummary = Boolean(job.jobSummary?.editable);
  // Save only has work to do when at least one of the drawer's inputs is editable.
  const canSaveFromDrawer = canEditSummary || canEditTicket;

  const [isUploading, setIsUploading] = useState(false);
  const [toDelete, setToDelete] = useState<TimesheetListItem | undefined>();
  const [isDeleting, setIsDeleting] = useState(false);
  // The timesheet the reviewer is viewing (null = closed). Opens a right-side
  // drawer — ticket image on the left, the Start/End/Total form on the right —
  // so they can read the paper ticket and key the times in without a second click.
  const [viewing, setViewing] = useState<TimesheetListItem | null>(null);
  const [isSavingTimes, setIsSavingTimes] = useState(false);
  // Draft of the slip number typed in the drawer, seeded from the open timesheet.
  const [timesheetNumberDraft, setTimesheetNumberDraft] = useState('');
  const summaryFormRef = useRef<JobSummaryEditFormHandler | null>(null);

  // AI-scan prefill: after upload we poll the new timesheet until it's scanned, then
  // open its viewer and prefill Start/End/Total from the extraction for confirmation.
  const [pendingScanTimesheetId, setPendingScanTimesheetId] = useState<string | null>(
    null,
  );
  const [pendingPrefill, setPendingPrefill] = useState<{
    loggingStartedAt: Dayjs | null;
    loggingEndedAt: Dayjs | null;
    approvedWorkTimeHours: number | null;
  } | null>(null);
  const isScanningTimesheet = pendingScanTimesheetId !== null;
  const aiPrefillRef = useRef<{
    approvedWorkTimeHours: number | null;
    loggingEndedAt: Dayjs | null;
    loggingStartedAt: Dayjs | null;
    timesheetId: string;
  } | null>(null);

  const handleUpload = useCallback(
    async (file: File) => {
      if (file.size / 1024 / 1024 > MAX_FILE_SIZE_IN_MB) {
        toasterStore.push(
          alert(
            t('file_upload.max_size', { maxSize: MAX_FILE_SIZE_IN_MB + ' MB' }),
            AlertTypes.error,
          ),
        );
        return;
      }
      if (!companyId) {
        return;
      }
      setIsUploading(true);
      try {
        if (useNewTimesheet) {
          const timesheet = await createTimesheet(companyId, { file, jobId: job.id });
          updateTimesheets(timesheetToListItem(timesheet));
          // Start polling the AI extraction so we can prefill the times once scanned.
          setPendingScanTimesheetId(timesheet.id);
        } else {
          // The category is what puts an attachment in this list, and it is only
          // settable on update — hence the two calls.
          const attachment = await createFileAttachmentOnJob(file, job.id);
          const categorized = await updateFileAttachmentById(attachment.id, {
            category: FileAttachmentCategory.TIMESHEET,
          });
          updateTimesheets(fileAttachmentToListItem(categorized));
        }
        toasterStore.push(
          alert(
            t('approvals.driver_day.time_sheet_added_successfully'),
            AlertTypes.success,
          ),
        );
      } finally {
        setIsUploading(false);
      }
    },
    [
      companyId,
      createTimesheet,
      createFileAttachmentOnJob,
      updateFileAttachmentById,
      useNewTimesheet,
      job.id,
      updateTimesheets,
      toasterStore,
    ],
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        handleUpload(file);
      }
    },
    [handleUpload],
  );

  const handleDelete = useCallback(async () => {
    if (!toDelete || !companyId) {
      return;
    }
    setIsDeleting(true);
    try {
      // Routed by the row, not the flag: the dual-read mixes both kinds, and their ids
      // come from different spaces.
      if (toDelete.source === 'timesheet') {
        await deleteTimesheetById(companyId, toDelete.deleteId);
      } else {
        await deleteFileAttachmentById(toDelete.deleteId);
      }
      updateTimesheets(toDelete, true);
      toasterStore.push(
        alert(
          t('approvals.driver_day.time_sheet_removed_successfully'),
          AlertTypes.success,
        ),
      );
      setToDelete(undefined);
    } finally {
      setIsDeleting(false);
    }
  }, [
    companyId,
    deleteTimesheetById,
    deleteFileAttachmentById,
    toDelete,
    updateTimesheets,
    toasterStore,
  ]);

  // Reseed the number draft each time a different timesheet is opened (and clear it on close),
  // so the input always reflects the sheet on screen rather than the previously viewed one.
  useEffect(() => {
    setTimesheetNumberDraft(viewing?.timesheetNumber ?? '');
  }, [viewing?.key, viewing?.timesheetNumber]);

  // Save the times typed alongside the ticket — same summary PATCH the hero's
  // inline edit uses — then let the parent refetch and close the drawer.
  const handleSaveTimes = useCallback(async () => {
    if (!viewing) {
      return;
    }
    // The times form only mounts when the reviewer may edit the summary; a user who can
    // only edit the slip number still saves through this same button.
    const data = canEditSummary ? await summaryFormRef.current?.onSubmit() : undefined;
    // The form resolves undefined when it fails validation, having already marked the
    // offending field. The number and the times share one Save button, so an invalid
    // time can't be allowed to discard a number the reviewer also typed: persist the
    // number anyway and leave the drawer open on the errors.
    const timesAreValid = !canEditSummary || Boolean(data);

    const trimmedNumber = timesheetNumberDraft.trim();
    // A legacy attachment has no Timesheet record to PATCH, so it carries no number.
    const numberChanged =
      canEditTicket &&
      viewing.source === 'timesheet' &&
      trimmedNumber !== (viewing.timesheetNumber ?? '');

    setIsSavingTimes(true);
    try {
      if (numberChanged && companyId) {
        try {
          // PATCH semantics: a cleared field is sent as null, not an empty string.
          const updated = await updateTimesheetById(companyId, viewing.deleteId, {
            timesheet_number: trimmedNumber || null,
          });
          const updatedRow = timesheetToListItem(updated);
          setTimesheetFiles((prev) =>
            prev.map((file) => (file.key === updatedRow.key ? updatedRow : file)),
          );
          // Move the open drawer onto the saved number too. Without this, a second save
          // in the same drawer still compares against the pre-save number, so typing the
          // old one back looks like a change to undo but sends no PATCH.
          setViewing((prev) =>
            prev?.key === updatedRow.key
              ? { ...prev, timesheetNumber: updatedRow.timesheetNumber }
              : prev,
          );
        } catch {
          // updateTimesheetById already surfaced the failure; stop before touching the
          // summary so a half-save can't report success.
          return;
        }
      }

      if (!timesAreValid) {
        return;
      }

      if (data) {
        await patchV1CompaniesCompanyIdJobsJobIdSummary({
          path: { 'company-id': companyId, 'job-id': job.id },
          body: {
            adjusted_logging_started_at: data.loggingStartedAt?.toISOString() ?? null,
            adjusted_logging_ended_at: data.loggingEndedAt?.toISOString() ?? null,
            approved_work_time_minutes: data.approvedWorkTimeMinutes,
          },
        });
        toasterStore.push(
          alert(t('approvals.approve_jobs.job_summary_updated'), AlertTypes.success),
        );

        const aiPrefill = aiPrefillRef.current;

        if (aiPrefill?.timesheetId === viewing.deleteId) {
          const isAiPrefillEdited =
            numberChanged ||
            data.loggingStartedAt?.valueOf() !== aiPrefill.loggingStartedAt?.valueOf() ||
            data.loggingEndedAt?.valueOf() !== aiPrefill.loggingEndedAt?.valueOf() ||
            minutesToDecimalHours(data.approvedWorkTimeMinutes) !==
              aiPrefill.approvedWorkTimeHours;

          AnalyticsController.track('ai_suggestion_resolved', {
            feature: 'timesheet',
            object_id: aiPrefill.timesheetId,
            outcome: isAiPrefillEdited ? 'edited' : 'accepted',
          });
          aiPrefillRef.current = null;
        }

        await onSummarySaved?.();
      }

      setViewing(null);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.jobs.failed_to_update_job_summary'),
      );
    } finally {
      setIsSavingTimes(false);
    }
  }, [
    canEditSummary,
    canEditTicket,
    companyId,
    job.id,
    onSummarySaved,
    timesheetNumberDraft,
    toasterStore,
    updateTimesheetById,
    viewing,
  ]);

  // Poll a just-uploaded timesheet for AI scan completion, then open its viewer and
  // prefill Start/End/Total from the extraction for the reviewer to confirm.
  useEffect(() => {
    if (!companyId || !pendingScanTimesheetId) {
      return;
    }
    let attempts = 0;
    const intervalId = setInterval(async () => {
      attempts += 1;
      if (attempts > MAX_SCAN_POLL_ATTEMPTS) {
        clearInterval(intervalId);
        setPendingScanTimesheetId(null);
        return;
      }
      const timesheet = await getTimesheetById(companyId, pendingScanTimesheetId, {
        silent: true,
      });
      if (!timesheet?.aiProcessed) {
        return;
      }
      clearInterval(intervalId);
      setPendingScanTimesheetId(null);

      // The list still holds the pre-scan upload row; swap in the scanned record so a
      // later open of the same sheet shows its shift times instead of dashes.
      const scannedRow = timesheetToListItem(timesheet);
      setTimesheetFiles((prev) =>
        prev.map((file) => (file.key === scannedRow.key ? scannedRow : file)),
      );

      const loggingStartedAt = timesheet.startedAt ?? null;
      const loggingEndedAt = timesheet.endedAt ?? null;
      const approvedWorkTimeHours = minutesToDecimalHours(timesheet.totalMinutes);

      if (!loggingStartedAt && !loggingEndedAt && approvedWorkTimeHours === null) {
        return;
      }
      setPendingPrefill({ loggingStartedAt, loggingEndedAt, approvedWorkTimeHours });
      aiPrefillRef.current = {
        approvedWorkTimeHours,
        loggingEndedAt,
        loggingStartedAt,
        timesheetId: timesheet.id,
      };
      setViewing(scannedRow);
    }, SCAN_POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [companyId, pendingScanTimesheetId, getTimesheetById]);

  // The viewer's edit form mounts only when a timesheet is open, so apply the prefill
  // once it's visible.
  // The drawer renders through a portal, which mounts its children one render after the
  // drawer opens, so an effect keyed on `viewing` alone can run before the form's ref
  // exists. Applying from the form's ref callback as well covers that late mount.
  const pendingPrefillRef = useRef(pendingPrefill);
  pendingPrefillRef.current = pendingPrefill;
  const applyPendingPrefill = useCallback(
    (form: JobSummaryEditFormHandler | null) => {
      if (!form || !pendingPrefillRef.current) {
        return;
      }
      form.prefill(pendingPrefillRef.current);
      pendingPrefillRef.current = null;
      setPendingPrefill(null);
      toasterStore.push(
        alert(t('approvals.driver_day.ai_shift_times_applied'), AlertTypes.info),
      );
    },
    [toasterStore],
  );
  const handleSummaryFormRef = useCallback(
    (form: JobSummaryEditFormHandler | null) => {
      summaryFormRef.current = form;
      applyPendingPrefill(form);
    },
    [applyPendingPrefill],
  );
  useEffect(() => {
    if (viewing) {
      applyPendingPrefill(summaryFormRef.current);
    }
  }, [viewing, pendingPrefill, applyPendingPrefill]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {!hideLabel && (
        // Section header — same treatment as the hero's "Hours" column header
        // (14px/600/treadBlack, sentence case), with a little air above it so it
        // reads as its own section under the times rather than another row label.
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 600,
            color: c.treadBlack,
            mt: 1,
            mb: 0.25,
          }}
        >
          {t('approvals.approve_jobs.summary_hero.timesheet')}
        </Typography>
      )}

      {loadingTimesheets ? (
        <CircularProgress
          size={14}
          sx={{ [`& .${circularProgressClasses.svg}`]: { color: c.treadGray5 } }}
        />
      ) : (
        timesheetFiles.map((ts) => (
          <TimesheetRow
            key={ts.key}
            timesheet={ts}
            // Legacy rows carry the API's own `deletable`, which TimesheetRow honors.
            canDelete={ts.source === 'timesheet' ? canDeleteTicket : true}
            onView={() => setViewing(ts)}
            onDelete={() => setToDelete(ts)}
          />
        ))
      )}

      {isScanningTimesheet && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <CircularProgress
            size={12}
            sx={{ [`& .${circularProgressClasses.svg}`]: { color: c.treadGray5 } }}
          />
          <Typography sx={{ fontSize: '11px', color: c.treadGray3 }}>
            {t('approvals.driver_day.scanning_timesheet')}
          </Typography>
        </Box>
      )}

      {/* Legacy uploads are not gated on canCreateTicket, matching legacy Approve Jobs. */}
      {(!useNewTimesheet || canCreateTicket) && (
        <SmallLoadingButton
          color="inherit"
          component="label"
          disableElevation
          loading={isUploading}
          size="small"
          variant="text"
          sx={{
            alignSelf: 'flex-start',
            flexShrink: 0,
            minWidth: 0,
            px: 0,
            fontWeight: 800,
            color: c.treadOrangeDark,
            '&:hover': { backgroundColor: 'transparent' },
          }}
        >
          + {t('actions.add_sheet')}
          <input
            accept="image/*"
            hidden
            onChange={handleChange}
            onClick={(event: React.MouseEvent<HTMLInputElement>) => {
              (event.target as HTMLInputElement).value = '';
            }}
            type="file"
          />
        </SmallLoadingButton>
      )}

      <DeleteTimesheetDialog
        isDeleting={isDeleting}
        onClose={() => setToDelete(undefined)}
        onDelete={handleDelete}
        timesheetId={toDelete?.deleteId}
      />

      {/* Ticket drawer — image on the left, Start/End/Total on the right, so the
          reviewer reads the paper ticket and keys the times in one place. */}
      <Drawer
        anchor="right"
        open={Boolean(viewing)}
        onClose={() => setViewing(null)}
        variant="temporary"
        sx={{
          [`& .${drawerClasses.paper}`]: {
            maxWidth: '1200px',
            overflow: 'hidden',
            width: '85%',
          },
        }}
      >
        {viewing && (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header */}
            <Box
              sx={{
                alignItems: 'center',
                borderBottom: `1px solid ${c.treadGray7}`,
                display: 'flex',
                flex: 0,
                gap: 1,
                p: 1.5,
              }}
            >
              <Typography
                title={viewing.fileName}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 14,
                  fontWeight: 700,
                  color: c.treadBlack,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {timesheetTitle(viewing)}
              </Typography>
              <IconButton
                aria-label={t('actions.download')}
                disabled={!viewing.fileUrl}
                onClick={() => viewing.fileUrl && window.open(viewing.fileUrl)}
                size="small"
              >
                <FileDownloadOutlined sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton
                aria-label={t('actions.close')}
                onClick={() => setViewing(null)}
                size="small"
              >
                <Close sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            {/* Body — ticket image (left) + times form (right) */}
            <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  overflow: 'auto',
                  backgroundColor: c.treadGray8,
                  p: 2,
                }}
              >
                {isPdf(viewing.fileName) ? (
                  <Box
                    component="embed"
                    src={viewing.fileUrl ?? undefined}
                    type="application/pdf"
                    sx={{ width: '100%', height: '100%', minHeight: '80vh', border: 0 }}
                  />
                ) : (
                  <Box
                    component="img"
                    alt={viewing.fileName}
                    src={viewing.fileUrl ?? undefined}
                    sx={{
                      width: '100%',
                      height: 'auto',
                      objectFit: 'contain',
                      display: 'block',
                    }}
                  />
                )}
              </Box>

              {/* Read-only reviewers still get the pane for a new-model sheet's shift times. */}
              {(canEditSummary || viewing.source === 'timesheet') && (
                <Box
                  sx={{
                    flex: '0 0 380px',
                    borderLeft: `1px solid ${c.treadGray7}`,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    overflow: 'auto',
                    p: 2,
                  }}
                >
                  {/* The slip number printed on the paper sheet — the value the office
                      reconciles against, so it sits above the times. Uses the v2 Label
                      (10px/700/treadBlack) so it matches the Start/End/Total Hrs labels
                      below it, which render in the same style via captionLabels. */}
                  {viewing.source === 'timesheet' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Label>{t('approvals.timesheets.timesheet_number_long')}</Label>
                      {canEditTicket ? (
                        <TextField
                          disabled={isSavingTimes}
                          fullWidth
                          slotProps={{
                            htmlInput: {
                              'aria-label': t(
                                'approvals.timesheets.timesheet_number_long',
                              ),
                            },
                          }}
                          onChange={(event) =>
                            setTimesheetNumberDraft(event.target.value)
                          }
                          placeholder={viewing.extractedNumber ?? undefined}
                          size="small"
                          value={timesheetNumberDraft}
                        />
                      ) : (
                        <Typography sx={{ fontSize: 14, color: c.treadBlack }}>
                          {viewing.timesheetNumber ?? NO_CONTENT}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {viewing.source === 'timesheet' && (
                    <Box
                      data-test-id="timesheet-shift-times"
                      sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
                    >
                      <Label>{t('approvals.timesheets.timesheet_times')}</Label>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <JobSummaryCardStat
                          primaryText={fmtTime(viewing.startedAt)}
                          sx={{ flex: 1, minWidth: 0 }}
                          title={t('approvals.timesheets.start')}
                        />
                        <JobSummaryCardStat
                          primaryText={fmtTime(viewing.endedAt)}
                          sx={{ flex: 1, minWidth: 0 }}
                          title={t('approvals.timesheets.end')}
                        />
                        <JobSummaryCardStat
                          primaryText={
                            minutesToDecimalHours(viewing.totalMinutes)?.toString() ??
                            NO_CONTENT
                          }
                          sx={{ flex: 1, minWidth: 0 }}
                          title={t('approvals.timesheets.total_hours')}
                        />
                      </Box>
                    </Box>
                  )}
                  {canEditSummary && (
                    <>
                      <Typography
                        sx={{ fontSize: 16, fontWeight: 700, color: c.treadBlack }}
                      >
                        {t('approvals.approve_jobs.summary_hero.hours')}
                      </Typography>
                      {/* Wrap the form so its flex:1 groups size to content (no tall-column
                          stretch) and sit at a tight, even rhythm. */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                        <JobSummaryEditForm
                          ref={handleSummaryFormRef}
                          approvedWorkTimeMinutes={
                            job.jobSummary?.approvedWorkTimeMinutes ??
                            job.jobSummary?.workTimeMinutes ??
                            null
                          }
                          approvedWorkTimeMinutesText={`${t('approvals.driver_day.breaks')}: ${job.jobSummary?.breakTimeMinutes ?? 0}min`}
                          captionLabels
                          isSubmittingForm={isSavingTimes}
                          loggingEndedAt={
                            job.jobSummary?.adjustedLoggingEndedAt ??
                            job.jobSummary?.loggingEndedAt ??
                            null
                          }
                          loggingEndedAtText={`${t('approvals.driver_day.last_geofence_entry')}: ${fmtTime(job.jobSummary?.lastGeofenceExitAt)}`}
                          loggingStartedAt={
                            job.jobSummary?.adjustedLoggingStartedAt ??
                            job.jobSummary?.loggingStartedAt ??
                            null
                          }
                          loggingStartedAtText={`${t('approvals.driver_day.first_geofence_entry')}: ${fmtTime(job.jobSummary?.firstGeofenceEntryAt)}`}
                        />
                      </Box>
                    </>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <SmallButton
                      color="inherit"
                      disableElevation
                      disabled={isSavingTimes}
                      onClick={() => setViewing(null)}
                      size="small"
                      variant="outlined"
                      sx={{ border: `1px solid ${c.treadGray7}` }}
                    >
                      {t('actions.cancel')}
                    </SmallButton>
                    {canSaveFromDrawer && (
                      <SmallLoadingButton
                        color="brandV2Green"
                        loading={isSavingTimes}
                        onClick={handleSaveTimes}
                        size="small"
                        startIcon={<Check />}
                        variant="contained"
                        sx={{ fontWeight: 700 }}
                      >
                        {t('actions.save')}
                      </SmallLoadingButton>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Drawer>
    </Box>
  );
}

// One attached timesheet. The whole row is the hit target — hovering lifts it onto a
// tinted, outlined surface and underlines the name, so it reads as "click to edit".
// Delete is tucked behind an ellipsis menu (the row-actions pattern used by the Files
// tables): removing a sheet is rare, so it doesn't earn a permanent red button.
function TimesheetRow({
  timesheet,
  canDelete,
  onView,
  onDelete,
}: {
  timesheet: TimesheetListItem;
  canDelete: boolean;
  onView: () => void;
  onDelete: () => void;
}) {
  const theme = useTheme();
  const c = theme.brandV2.colors;
  const menuHandler = useRef<MenuHandler>(null);
  const deletable = timesheet.deletable === true && canDelete;
  const label = timesheetTitle(timesheet);

  const tile = {
    width: 30,
    height: 22,
    borderRadius: '3px',
    border: `1px solid ${c.treadGray7}`,
    flexShrink: 0,
  } as const;

  return (
    <Box
      aria-label={`${label} — ${t('approvals.approve_jobs.summary_hero.timesheet_edit_hint')}`}
      onClick={onView}
      onKeyDown={(event) => {
        // Only the row itself opens the editor. A key pressed on the actions ellipsis
        // bubbles up here, and preventDefault() would cancel the click the browser
        // generates from Enter/Space on that button — leaving the menu unopenable by
        // keyboard while the editor opened behind it.
        if (event.target !== event.currentTarget) {
          return;
        }
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onView();
        }
      }}
      role="button"
      tabIndex={0}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.875,
        minWidth: 0,
        px: 0.75,
        py: 0.5,
        mx: -0.75,
        borderRadius: 1,
        border: '1px solid transparent',
        cursor: 'pointer',
        transition: 'background-color 120ms ease, border-color 120ms ease',
        '&:hover, &:focus-visible': {
          bgcolor: c.treadGray8,
          borderColor: c.treadGray7,
        },
        '&:hover .timesheet-row__label': { textDecoration: 'underline' },
      }}
    >
      <Box sx={{ ...tile, display: 'grid', placeItems: 'center', color: c.treadGray4 }}>
        <InsertDriveFileOutlined sx={{ fontSize: 14 }} />
      </Box>
      <Typography
        className="timesheet-row__label"
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: '11.5px',
          fontWeight: 600,
          color: c.treadBlue,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
      {deletable && (
        // Menu stops the trigger click from reaching the row, so opening the menu
        // never also opens the editor behind it.
        <Menu
          ref={menuHandler}
          menuTrigger={
            <IconButton
              aria-label={t('approvals.approve_jobs.summary_hero.timesheet_actions')}
              size="small"
              sx={{
                p: 0.25,
                color: c.treadGray4,
                flexShrink: 0,
                '&:hover': { color: c.treadBlack },
              }}
            >
              <MoreHoriz sx={{ fontSize: 16 }} />
            </IconButton>
          }
        >
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              onDelete();
              menuHandler.current?.onClose?.();
            }}
          >
            <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, width: '100%' }}>
              <DeleteOutlined sx={{ fontSize: '16px' }} />
              <Typography sx={{ color: c.treadBlack, fontSize: '12px' }}>
                {t('actions.delete')}
              </Typography>
            </Box>
          </MenuItem>
        </Menu>
      )}
    </Box>
  );
}
